import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import moment from 'moment';

import type { PopupData, Recipient, Service, Slack } from 'webapp-types';
import { BillingFeature, ModelName, SlackMessage, UserLevel } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { getInitials, sortBy } from 'webapp/utils/common';

import UserPopupInviteComponent from 'webapp/components/user/popup-invite';
import UserPopupInviteLimitationComponent from 'webapp/components/user/popup-invite-limitation';
import UserPopupSelectComponent from 'webapp/components/user/popup-select';

import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';
import User from 'webapp/models-custom/user';
import type UserRecord from 'webapp/models/user';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';

export interface UserDataForList extends LabelValuePairString {
	label2: string;
	group?: string; // 'Agency users' | 'Client users'
	avatar?: string;
	initials?: string;
}

export default class ModelManagerUsersService extends ModelManagerForStoreService<
	UserRecord,
	User
> {
	@service('billing') declare billingService: Service.Billing;
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('request') declare requestService: Service.Request;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;
	@service('analytics') declare analyticsService: Service.Analytics;

	@cached
	get usersWithoutAgencyAdminsRecords(): UserRecord[] {
		let users = this.savedRecords;
		if (!this.accountService.isAgency) {
			users = users.filter((u) => !u.isRestricted);
		}
		return users;
	}

	@cached
	get activatedUserRecords(): UserRecord[] {
		return this.savedRecords.filter((u) => u.activated);
	}

	@cached
	get activatedUsersWithoutAgencyAdminsRecords(): UserRecord[] {
		let users = this.activatedUserRecords;
		if (!this.accountService.isAgency) {
			users = users.filter((u) => !u.isRestricted);
		}
		return users;
	}

	@cached
	get activatedUsersList(): UserDataForList[] {
		const activatedUserRecords = this.activatedUserRecords,
			isGrouped =
				!this.accountService.isAgency && activatedUserRecords.some((u) => u.isRestricted);
		return activatedUserRecords.map((user) => this.getDataForList(user, isGrouped));
	}

	@cached
	get activatedAndVerifiedUsersWithoutViewersList(): UserDataForList[] {
		const activatedUserRecords = this.activatedUserRecords,
			isGrouped =
				!this.accountService.isAgency && activatedUserRecords.some((u) => u.isRestricted);
		return activatedUserRecords
			.filter((user) => user.level !== UserLevel.VIEWER && user.isVerified)
			.map((user) => this.getDataForList(user, isGrouped));
	}

	@cached
	get activatedUsersWithoutAgencyAdminsList(): UserDataForList[] {
		return this.activatedUsersWithoutAgencyAdminsRecords.map((user) =>
			this.getDataForList(user),
		);
	}

	get activatedAdminsWithoutAgencyAdminsList(): UserDataForList[] {
		return this.activatedUsersWithoutAgencyAdminsRecords
			.filter((user) => user.level === UserLevel.ADMIN)
			.map((user) => this.getDataForList(user));
	}

	@cached
	get usersWithoutAgencyAdminsModels(): User[] {
		return this.createModelsFromRecordsArray(this.usersWithoutAgencyAdminsRecords);
	}

	@cached
	get activatedUserModels(): User[] {
		return this.createModelsFromRecordsArray(this.activatedUserRecords);
	}

	private getDataForList(user: UserRecord, isGrouped = false): UserDataForList {
		const userData: UserDataForList = {
			value: user.id!,
			label: user.name,
			label2: user.email,
		};

		if (isGrouped) {
			userData.group = user.isRestricted ? 'Organization users' : 'Account users';
		}

		if (user.avatar) {
			userData.avatar = user.avatar;
		} else if (user.name) {
			userData.initials = getInitials(user.name);
		}
		return userData;
	}

	get inactiveInvitedUserRecords(): UserRecord[] {
		if (this.currentUserService.isAdmin) {
			const users = this.activatedUserRecords.filter((user) => {
				return (
					!user.lastSeen &&
					!user.lastLoginMobile &&
					user.activated &&
					!user.isRestricted && // dont show agency users as inactive in client spaces
					moment().unix() - user.created > 5 * 24 * 60 * 60 &&
					(!user.resendInvitationMailCount || user.resendInvitationMailCount < 5)
				);
			});
			return sortBy(users, 'created');
		} else {
			return [];
		}
	}

	get inactiveInvitedUserRecordsForBanner(): UserRecord[] {
		return this.inactiveInvitedUserRecords.filter((user) => {
			return (
				moment().unix() - user.created < 20 * 24 * 60 * 60 &&
				(!user.resendInvitationMailCount || user.resendInvitationMailCount < 1)
			);
		});
	}

	@cached
	get agencyAdminRecordForClientSpace(): UserRecord {
		let agencyAdmin;

		const agencyAdmins = this.savedRecords.filter((u) => u.isRestricted),
			ownerId = this.accountService.ownerId,
			parentOwnerId = this.accountService.parentOwnerId;

		// if no account manager, use agency's owner
		if (ownerId || parentOwnerId) {
			agencyAdmin = agencyAdmins.find((item) => item.id === String(ownerId || parentOwnerId));
		}

		if (!agencyAdmin) {
			// fallback to oldest user from the agency
			agencyAdmin = sortBy(agencyAdmins, 'created')[0]!;
		}

		return agencyAdmin;
	}

	checkToOpenInvitePopup(trackingOrigin?: string): void {
		if (this.currentUserService.isDemo) {
			return;
		}

		if (!this.accountService.canInviteUsers) {
			this.openUserInviteLimitationPopup();
			return;
		}

		if (this.currentUserService.isVerified) {
			if (this.billingService.checkIfCan(BillingFeature.USERS, true)) {
				if (trackingOrigin) {
					this.analyticsService.event(TrackingEvent.NEW_USER, {
						origin: trackingOrigin,
					});
				}

				this.openUserInvitePopup();
			}
		} else {
			this.currentUserService.openEmailVerificationInfoPopup('invite new users');
		}
	}

	sendUserInviteReminder(userId: string): void {
		const url = `/users/${userId}/invite-reminder`;
		this.requestService
			.api('POST', url)
			.then(() => {
				const userRecord = this.activatedUserRecords.find(
					(item) => item.id === String(userId),
				);
				if (userRecord) {
					const count = userRecord.resendInvitationMailCount || 0;
					userRecord.resendInvitationMailCount = count + 1;
				}
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
		this.infolayerService.ok('Reminder sent!');
	}

	openUserInvitePopup(data?: PopupData.User.UserInvite): void {
		const isPopupOpen = this.popupService.isOpen(UserPopupInviteComponent);

		if (!isPopupOpen) {
			this.popupService
				.createModal(UserPopupInviteComponent, {
					popupClass: `popup-invite-users-${this.currentUserService.isAdmin ? 'admin' : 'non-admin'}`,
					allowStacked: true, // used for role-select popup
				})
				.open(data);
		}
	}

	openUserInviteLimitationPopup(): void {
		this.popupService
			.createModal(UserPopupInviteLimitationComponent, {
				popupClass: 'popup-type-invite-limitation',
			})
			.open();
	}

	openUserInvitePopupWithEligibilityChecks(data?: PopupData.User.UserInvite) {
		if (this.currentUserService.isVerified) {
			if (this.billingService.checkIfCan(BillingFeature.USERS, true)) {
				this.openUserInvitePopup(data);
			}
		} else {
			this.currentUserService.openEmailVerificationInfoPopup('invite new users');
		}
	}

	openUserSelectPopup(data: PopupData.User.Select) {
		const popup = this.popupService.createModal(UserPopupSelectComponent, {
			popupClass: 'popup-type-user-select',
		});
		popup.open(data);
		return popup;
	}

	getFirstName(name: string): string {
		const parts = (name || '').split(' ');
		return parts[0]!;
	}

	getFilterOptionsForUsers(allItemCountByUser: Record<number, number>): FilterOption<number>[] {
		// given: { <userId> : <count>}

		const activeUserId = this.currentUserService.numericId!;

		const list: FilterOption<number>[] = [
			{ id: 0, text: 'Everyone', notCombinable: true },
			{ id: activeUserId, text: 'Me', notCombinable: true },
			{ id: -1, text: 'All others', notCombinable: true },
		];

		let otherUsers: FilterOption<number>[] = [];

		this.activatedUserRecords.forEach((user: UserRecord) => {
			const userId = +user.id!;
			if (userId !== activeUserId) {
				otherUsers.push({
					id: userId,
					text: user.name,
					img: user.avatar || '',
					used: allItemCountByUser[userId] || 0,
				});
			}
		});

		otherUsers = otherUsers.filter((item) => item.used);

		otherUsers.sort((a, b) => {
			const aUsed = a.used || 0,
				bUsed = b.used || 0;
			if (aUsed > bUsed) {
				return -1;
			}
			if (aUsed < bUsed) {
				return 1;
			}
			return 0;
		});

		list.push(...otherUsers);

		return list;
	}

	getMappedRecipients(
		emails: string[],
		userIds: number[] = [],
		slackChannels: Slack.Channels = [],
		addMe = true,
	): Recipient[] {
		const myEmail = this.currentUserService.model?.email || '';
		const myId = this.currentUserService.numericId!;
		const otherEmails = emails.filter((email: string) => email !== myEmail);
		const otherIds = userIds.filter((uId) => +uId !== myId);

		const emailRecipients = otherEmails.map((email: string) => {
			const user = this.activatedUserRecords.find((u) => u.email === email);
			return {
				isDatabox: !!user,
				name: user?.name || email,
				email,
			};
		});

		const userIdRecipients = otherIds.map((userId: number) => {
			const user = this.activatedUserRecords.find((u) => u.id === String(userId));
			return {
				isDatabox: !!user,
				name: user?.name,
				email: user?.email,
			};
		});

		const recipients = [...userIdRecipients, ...emailRecipients];

		if (addMe || emails.includes(myEmail) || userIds.includes(myId)) {
			recipients.unshift({
				isDatabox: true,
				name: 'Me',
				email: myEmail,
			});
		}

		const channels = [];

		const isSlackDirectMessage = !!(slackChannels || []).find(
			(item) => item.id === SlackMessage.DIRECT,
		);
		if (isSlackDirectMessage) {
			channels.push({
				isSlack: true,
				name: 'DM',
			});
		}

		(slackChannels || []).forEach((item) => {
			if (item.id !== SlackMessage.DIRECT) {
				channels.push({
					isSlack: true,
					name: `#${(item as Slack.Channel).name}`,
				});
			}
		});

		return [...recipients, ...channels];
	}

	// ------ For-store ------

	modelName = ModelName.USER;

	createModel(model: UserRecord): User {
		return new User({ model });
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/users': ModelManagerUsersService;
	}
}
